// The console's frame: the rail, the topbar, the centred card, and the small
// pieces every screen is built from (#231, #258, #414).
//
// Brief's light rail carries the workspace and acting organisation, with a
// context header and grouped navigation from the destination registry. Its
// shared visual vocabulary lives in `styles/console.css`.
// **There is no colour and no font in this file** — a
// component names a class, and the stylesheet is the one place a hex value
// lives (#414; the reader is `styles.test.ts`).
//
// **The rail renders `DESTINATIONS` and nothing else** (#258): a destination and
// the screen it renders are one entry in `routes.ts`, so the rail can grow
// without ever naming a screen that is not there.

import {
  useEffect,
  useId,
  useRef,
  useState,
  type ComponentPropsWithRef,
  type ReactNode,
  type Ref,
} from "react";

import { isPlatformViewer, type SessionResponse } from "../api/session";
import type { ContextChoice } from "../api/session";
import type { OrgRow } from "../api/customers";
import { choiceFor, currentMembership, orgKindName, orgKindOf, type Membership } from "../api/memberships";
import type { RunRow } from "../api/runs";
import { CommandMenu } from "./CommandMenu";
import { ReadOnlyContext } from "./readOnly";
import { SessionContext } from "./sessionContext";
const logo = new URL("./torque-logo.svg", import.meta.url).href;
import {
  DESTINATIONS,
  GROUPS,
  destinationOf,
  groupHeading,
  visibleDestinations,
  type DestinationId,
} from "./routes";

export function Chip({ children, tone }: { children: ReactNode; tone?: "good" | "bad" | "warn" }) {
  const cls = tone === undefined ? "tag" : `tag tag--${tone}`;
  return (
    <span className={cls}>
      {tone === undefined ? null : <i className="dot" aria-hidden="true" />}
      {children}
    </span>
  );
}

/** A message the person must read before anything else on the screen. */
export function Notice({
  children,
  tone,
  id,
  focusRef,
  standing = false,
}: {
  children: ReactNode;
  tone?: "bad" | "good";
  /** For a control that `aria-describedby` this notice. */
  id?: string;
  /** For a notice focus moves to once the thing it reports has happened (#584). */
  focusRef?: Ref<HTMLParagraphElement>;
  /** A fact of the page rather than news, read where it stands and never announced
   *  assertively, whatever its tone (#1374). */
  standing?: boolean;
}) {
  const cls = tone === undefined ? "notice" : `notice notice--${tone}`;
  return (
    <p
      id={id}
      role={tone === "bad" && !standing ? "alert" : "status"}
      className={cls}
      ref={focusRef}
      tabIndex={focusRef === undefined ? undefined : -1}
    >
      {children}
    </p>
  );
}

export function Field({
  label,
  id,
  hint,
  error,
  reveal = false,
  ...input
}: {
  label: string;
  id: string;
  /**
   * A line under the input that says what goes in it. A node, not only a
   * string, so a hint can carry the link to where the value is made (#578).
   */
  hint?: ReactNode;
  /** The field's own failure, read aloud with the field. */
  error?: string | null;
  /**
   * A Show/Hide button beside the input (#571). For a secret that is typed or
   * pasted once and then sealed: a person cannot check what is about to go in
   * unless they can read it back, and the paste that saved a connection holding
   * a different string than the one on screen is what this exists for. Hidden
   * again on every render the page does not keep.
   */
  reveal?: boolean;
} & ComponentPropsWithRef<"input">) {
  const [shown, setShown] = useState(false);
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const described = [hintId, errorId].filter(Boolean).join(" ") || undefined;
  const field = (
    <input
      id={id}
      className="input"
      aria-describedby={described}
      aria-invalid={error ? true : undefined}
      {...input}
      type={reveal && shown ? "text" : input.type}
    />
  );
  return (
    <p className="field">
      <label htmlFor={id} className="field__label">
        {label}
      </label>
      {reveal ? (
        <span className="field__control">
          {field}
          <button
            type="button"
            className="btn btn--small"
            aria-controls={id}
            aria-pressed={shown}
            onClick={() => setShown((was) => !was)}
          >
            {shown ? "Hide" : "Show"}
          </button>
        </span>
      ) : (
        field
      )}
      {hint ? (
        <small id={hintId} className="field__hint">
          {hint}
        </small>
      ) : null}
      {error ? (
        <small id={errorId} className="field__error" role="alert">
          {error}
        </small>
      ) : null}
    </p>
  );
}

/** `Field`'s label and styling around a choice from a list, rather than a typed value. */
export function SelectField({
  label,
  id,
  children,
  ...select
}: { label: string; id: string; children: ReactNode } & ComponentPropsWithRef<"select">) {
  return (
    <p className="field">
      <label htmlFor={id} className="field__label">
        {label}
      </label>
      <select id={id} className="input" {...select}>
        {children}
      </select>
    </p>
  );
}

/** The wordmark and its three-bar mark, in the rail and above the signed-out card. */
export function Brand({ href = "/" }: { href?: string | null }) {
  const mark = <img src={logo} alt="Torque Assist" width="164" height="42" />;
  // No link where there is nowhere to go: the screen an account being deleted is shown (#1136).
  if (href === null) return <span className="brand">{mark}</span>;
  return (
    <a className="brand" href={href}>
      {mark}
    </a>
  );
}

/**
 * The signed-out screens: one card, centred, the brand above it, nothing else.
 *
 * `wide` widens the column for a screen whose content is rows rather than a
 * form. The 420px default is a sign-in form's width and is right for one; an
 * invitation card inside it wraps an email address onto three lines (#440).
 */
export function Centre({
  title,
  lede,
  wide = false,
  brandHref = "/",
  children,
}: {
  title: string;
  lede?: ReactNode;
  wide?: boolean;
  /** Where the brand leads; null draws it as a mark and not a link. */
  brandHref?: string | null;
  children: ReactNode;
}) {
  const heading = useRef<HTMLHeadingElement>(null);
  useEffect(() => { heading.current?.focus(); }, [title]);
  return (
    <main className="centre">
      <div className={wide ? "centre__inner centre__inner--wide fade" : "centre__inner fade"}>
        <Brand href={brandHref} />
        <section className="card">
          <h1 ref={heading} tabIndex={-1}>{title}</h1>
          {lede ? <p className="lede">{lede}</p> : null}
          {children}
        </section>
      </div>
    </main>
  );
}

/** The first letter of a name, for the workspace block and the picker. */
export function initialOf(name: string): string {
  const trimmed = name.trim();
  return trimmed === "" ? "?" : trimmed[0]!;
}

/**
 * One drawing per destination (#803), each in the same 24-unit, 1.5-stroke line
 * style. A `Record` over `DestinationId`, so a destination added to the registry
 * without an icon does not type-check, and there is no shared fallback to fall
 * back to.
 */
export const NAV_ICONS: Readonly<Record<DestinationId, string>> = {
  // A house: the workspace's front page.
  overview: "M3 11l9-8 9 8 M5 9v12h14V9 M10 21v-6h4v6",
  // A signal trace: an alert followed through.
  runs: "M2 12h4l3-8 5 16 3-8h5",
  // Two people: the customers an organisation serves.
  customers:
    "M9 11a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7z M2.5 20c0-3.5 2.9-6 6.5-6s6.5 2.5 6.5 6 M16 4.3a3.5 3.5 0 0 1 0 6.4 M18.5 14.5c1.8.9 3 2.8 3 5.5",
  inbox: "M3 13h5l1.5 3h5L16 13h5 M5 5h14l2 8v6H3v-6z",
  // A building: an organisation on the platform.
  organisations: "M4 21V3h10v18 M14 9h6v12 M2 21h20 M8 7h2 M8 11h2 M8 15h2 M17 13v.01 M17 17v.01",
  // A shield with a tick: how well defended the workspace is.
  posture: "M12 3l8 4v5c0 5-8 9-8 9s-8-4-8-9V7z M8 12l3 3 5-6",
  accounts: "M3 4h18v6H3z M3 14h18v6H3z M6 7h2 M6 17h2",
  // A tree of accounts under one root: an AWS Organization.
  awsOrganization: "M9 3h6v4H9z M3 17h6v4H3z M15 17h6v4h-6z M12 7v5 M6 12h12 M6 12v5 M18 12v5",
  sources: "M12 3v5 M12 16v5 M3 12h5 M16 12h5 M8 8h8v8H8z",
  // Two links of a chain: another tool joined to this one.
  integrations: "M10 14a4 4 0 0 0 5.7 0l3-3a4 4 0 0 0-5.7-5.7l-1 1 M14 10a4 4 0 0 0-5.7 0l-3 3a4 4 0 0 0 5.7 5.7l1-1",
  // A chip: the model that does the reasoning.
  provider: "M7 7h10v10H7z M10 10h4v4h-4z M10 3v4 M14 3v4 M10 17v4 M14 17v4 M3 10h4 M3 14h4 M17 10h4 M17 14h4",
  // A key: what a partner has been let in to do.
  access: "M8 11a4 4 0 1 0 0 8 4 4 0 0 0 0-8z M10.8 12.2L20 3 M17 6l2.5 2.5 M14.5 8.5l2 2",
  // A card: the bill and what used it.
  spend: "M3 6h18v12H3z M3 10h18 M7 15h4",
  // A page with a folded corner and its lines: the record.
  audit: "M6 3h9l4 4v14H6z M14 3v5h5 M9 12h7 M9 16h7",
  // A person beside a list: who is in the workspace, and what each may do.
  team: "M8 11a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7z M2 20c0-3.5 2.7-6 6-6s6 2.5 6 6 M16 8h6 M16 12h6 M18 16h4",
  // A clipboard with a tick: what a proposed change must pass.
  policy: "M9 3h6v3H9z M7 5H5v16h14V5h-2 M9 14l2 2 4-4",
  // A person in a circle: the one signed in.
  account: "M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18z M12 12a3 3 0 1 0 0-6 3 3 0 0 0 0 6z M6.5 18.5c1.2-2 3.2-3 5.5-3s4.3 1 5.5 3",
};

function NavIcon({ id }: { id: DestinationId }) {
  return (
    <svg
      className="nav-icon"
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d={NAV_ICONS[id]} />
    </svg>
  );
}

/** How many are waiting behind a rail entry; nothing at all when none are. */
function NavCount({ count }: { count: number | undefined }) {
  if (count === undefined || count <= 0) return null;
  return (
    <span className="nav-count" aria-label={`${count} waiting`}>
      {count}
    </span>
  );
}

/**
 * The avatar menu (#795, decision 1; #804): what is the person's own, apart from the
 * workspace the rail is about. A disclosure rather than an ARIA menu — a button
 * that shows a list of links and one button — so Tab and Enter are the whole
 * of driving it, and Escape closes it and hands focus back to the button.
 * Closed, its list is `hidden`: out of sight and out of the tab order.
 */
function AccountMenu({
  email,
  current,
  onSignOut,
}: {
  email: string;
  /** Whether this location is Your account, which the list then marks. */
  current: boolean;
  onSignOut: () => void;
}) {
  const [open, setOpen] = useState(false);
  const account = destinationOf("account");
  const holder = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!open) return;
    const away = (event: PointerEvent) => {
      if (!holder.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("pointerdown", away);
    return () => document.removeEventListener("pointerdown", away);
  }, [open]);
  return (
    <div
      ref={holder}
      className="account-menu"
      onKeyDown={(event) => {
        if (event.key !== "Escape" || !open) return;
        setOpen(false);
        button.current?.focus();
      }}
      onBlur={(event) => {
        if (!holder.current?.contains(event.relatedTarget as Node | null)) setOpen(false);
      }}
    >
      <button
        ref={button}
        type="button"
        className="account-menu__button"
        aria-label={`Account menu for ${email}`}
        aria-expanded={open}
        aria-controls="account-menu"
        onClick={() => setOpen((was) => !was)}
      >
        <span aria-hidden="true">{initialOf(email)}</span>
      </button>
      <div id="account-menu" className="account-menu__list" hidden={!open}>
        <p className="account-menu__email">{email}</p>
        <ul>
          <li>
            {current ? (
              <span aria-current="page">{account.label}</span>
            ) : (
              <a href={account.path}>{account.label}</a>
            )}
          </li>
          <li>
            <button type="button" className="link" onClick={onSignOut}>
              Sign out
            </button>
          </li>
        </ul>
      </div>
    </div>
  );
}

/**
 * The console proper: the rail says whose runs these are, where else you can
 * go, and how to leave; the topbar says where you are.
 *
 * `current` is the destination the location resolved to, or null when the
 * screen is under none of them — the run page, which is reached from a row.
 * Marking a rail entry current there would tell a person the rail took them
 * somewhere it did not.
 *
 * `contextName` is what the rail calls the workspace: a name when the
 * membership list holds it, the id when it does not (`api/memberships.ts`).
 */
export function Shell({
  session,
  current,
  contextName,
  actingOrg = null,
  onSignOut,
  runs = [],
  memberships = [],
  orgs = null,
  onChooseContext,
  counts = {},
  contextRefused = null,
  children,
}: {
  session: SessionResponse;
  current: DestinationId | null;
  contextName: string;
  /** The partner whose access opened this workspace, when it is a visit (#419). */
  actingOrg?: string | null;
  onSignOut: () => void;
  runs?: readonly RunRow[];
  memberships?: readonly Membership[];
  /** The platform's org list, where a platform viewer's org context takes its kind from (#1205). */
  orgs?: readonly OrgRow[] | null;
  onChooseContext?: (choice: ContextChoice) => Promise<void>;
  /** A number beside a rail entry, shown when above zero: the inbox's waiting items (#495). */
  counts?: Partial<Record<DestinationId, number>>;
  /** The last context selection was refused (#971): said here, with the way back to the switch. */
  contextRefused?: "workspace" | "org" | null;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const [contextError, setContextError] = useState<string | null>(null);
  const contextSwitch = useRef<HTMLSelectElement | null>(null);
  const switchable =
    onChooseContext !== undefined &&
    (memberships.length > 1 ||
      (memberships.length === 1 && currentMembership(session, [...memberships]) === null));
  const here = DESTINATIONS.find((d) => d.id === current)?.label ?? "Investigation";
  const railId = useId();
  const orgKind = orgKindOf(session, memberships, orgs);
  const kindLabel = session.workspace_id !== null ? "Workspace" : orgKindName(orgKind);
  const readOnly = isPlatformViewer(session);
  const destinations = visibleDestinations(session);
  const groups = GROUPS.filter((group) =>
    destinations.some((destination) => destination.group === group),
  );
  return (
    <div className="shell">
      <a href="#main" className="skip-link">
        Skip to content
      </a>
      <aside id="rail" className={open ? "rail is-open" : "rail"} aria-label="Main navigation">
        <Brand />
        <div className="workspace">
          <span className="initial" aria-hidden="true">
            {initialOf(contextName)}
          </span>
          <span>
            <strong>{contextName}</strong>
            <small>
              {kindLabel} · {session.role ?? "no role"}
              {actingOrg === null ? null : <> · via {actingOrg}</>}
            </small>
          </span>
        </div>
        {switchable ? (
          <label className="context-switch field__label">
            Switch workspace
            <select
              ref={contextSwitch}
              className="input"
              value=""
              onChange={(event) => {
                const membership = memberships[Number(event.target.value)];
                if (membership)
                  void onChooseContext(choiceFor(membership)).catch(() =>
                    setContextError("Could not switch workspace. Please try again."),
                  );
              }}
            >
              <option value="">Pick a workspace, partner or customer</option>
              {memberships.map((membership, index) => (
                <option key={index} value={index}>
                  {membership.name} · {membership.kind === "workspace" ? "workspace" : orgKindName(membership.org_kind)}
                </option>
              ))}
            </select>
          </label>
        ) : null}
        {contextError ? <Notice tone="bad">{contextError}</Notice> : null}
        {actingOrg === null ? null : (
          <p className="rail-note">
            You are in this customer's workspace through {actingOrg}'s partner access. Its own decisions
            stay its own: partner access, data access, the provider, the team.
          </p>
        )}
        <nav className="nav" aria-label="Console">
          {groups.map((group) => (
            <div key={group} className="nav-group" role="group" aria-labelledby={`${railId}-${group}`}>
              <div className="nav-label" id={`${railId}-${group}`}>
                {groupHeading(group, session, orgKind)}
              </div>
              {destinations
                .filter((destination) => destination.group === group)
                .map((destination) =>
                  destination.id === current ? (
                    <span key={destination.id} aria-current="page" className="is-current">
                      <NavIcon id={destination.id} />
                      {destination.label}
                      <NavCount count={counts[destination.id]} />
                    </span>
                  ) : (
                    // A plain anchor: the control plane serves the page behind every
                    // path it does not claim, so a destination needs no router to
                    // reach (`control_plane/api.py`).
                    <a key={destination.id} href={destination.path} onClick={() => setOpen(false)}>
                      <NavIcon id={destination.id} />
                      {destination.label}
                      <NavCount count={counts[destination.id]} />
                    </a>
                  ),
                )}
            </div>
          ))}
        </nav>
      </aside>
      <div
        className={open ? "rail-scrim is-open" : "rail-scrim"}
        onClick={() => setOpen(false)}
        aria-hidden="true"
      />
      <div className="app">
        <header className="topbar">
          <div className="breadcrumb">
            <button
              type="button"
              className="btn btn--quiet rail-toggle"
              aria-controls="rail"
              aria-expanded={open}
              aria-label={open ? "Close navigation" : "Open navigation"}
              onClick={() => setOpen((was) => !was)}
            >
              ☰
            </button>
            <strong>{contextName}</strong>
            <span className="slash" aria-hidden="true">
              /
            </span>
            <span>{here}</span>
          </div>
          <div className="topbar__tools">
            <CommandMenu session={session} runs={runs} />
            <AccountMenu email={session.user.email} current={current === "account"} onSignOut={onSignOut} />
          </div>
        </header>
        <main id="main" className="content fade">
          {contextRefused ? (
            // The same words whether or not that workspace exists: the refusal does not say,
            // and nor does this (#971).
            <Notice tone="bad">
              {contextRefusalWords(contextRefused)}{" "}
              {switchable ? (
                <button
                  type="button"
                  className="btn btn--small"
                  onClick={() => {
                    setOpen(true);
                    contextSwitch.current?.focus();
                  }}
                >
                  Choose a workspace
                </button>
              ) : (
                <a href="/">Back to {contextName}</a>
              )}
            </Notice>
          ) : null}
          {session.acting_org_id && onChooseContext ? (
            <div className="customer-visit">
              <div><strong>Visiting a customer workspace</strong><p>Visiting for {actingOrg}. Access is limited to what this customer allows.</p></div>
              <button type="button" className="btn btn--small" onClick={() => {
                void onChooseContext({ org_id: session.acting_org_id! })
                  .then(() => window.location.assign("/customers"))
                  .catch(() => setContextError("Could not return to your partner's customers. Retry or sign in again."));
              }}>Return to customers</button>
            </div>
          ) : null}
          {session.impersonated_by ? (
            // Who is being impersonated, and by whom, on every page (#804): it
            // was said on Settings alone, which is not where an operator stays.
            <div className="platform-view" role="status">
              <strong>
                Viewing as {session.user.email}, impersonated by {session.impersonated_by}
              </strong>
            </div>
          ) : null}
          {readOnly ? (
            <div className="platform-view" role="status">
              <strong>Viewing as platform admin (read-only)</strong>
              <p>
                You hold no membership in this {kindLabel.toLowerCase()}, so you can read it but not
                change it; acting here needs a membership. Your entry is on its audit log.
              </p>
            </div>
          ) : null}
          <SessionContext.Provider value={session}>
            <ReadOnlyContext.Provider key={String(session.session?.session_id ?? "")} value={readOnly}>{children}</ReadOnlyContext.Provider>
          </SessionContext.Provider>
        </main>
      </div>
    </div>
  );
}

/**
 * The title bar of a destination: the name, then the action on the right. The
 * heading is the destination's label, read from the registry, so a screen
 * cannot name itself differently from the rail that led to it (#803). Nothing
 * else sits here: no lede and no group eyebrow (#799).
 */
export function PageIntro({
  destination,
  action,
}: {
  destination: DestinationId;
  action?: ReactNode;
}) {
  return (
    <div className="page-intro">
      <h1>{destinationOf(destination).label}</h1>
      {action ?? null}
    </div>
  );
}

/** A screen's "nothing here yet": says so, and what would put something here. */
export function Empty({
  title,
  children,
  mark,
}: {
  title: string;
  children?: ReactNode;
  mark?: string;
}) {
  return (
    <div className="empty">
      {mark ? (
        <div className="empty__mark" aria-hidden="true">
          {mark}
        </div>
      ) : null}
      <h2>{title}</h2>
      {children}
    </div>
  );
}

export function contextRefusalWords(kind: "workspace" | "org"): string {
  return kind === "workspace"
    ? "You're not a member of that workspace."
    : "You don't have access to that partner or customer.";
}
